import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import LayoutWithNavbar from './LayoutWithNavbar';
import { fetchInvoiceDetailsByGstid, markInvoiceAsRemoved, updateInvoiceNo } from '../services/api';
import { FaFileExcel, FaTrash, FaEdit } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const InvoiceDetailPage = () => {
  const { gstid } = useParams();
  const [invoices, setInvoices] = useState([]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stateId = searchParams.get('state');

  // Edit InvoiceNo modal
  const [editModal, setEditModal] = useState({ open: false, id: null, value: '' });

  // Remove (reason) modal
  const [removeModal, setRemoveModal] = useState({ open: false, invoiceId: null, reason: '' });

  const loadDetails = async () => {
    const res = await fetchInvoiceDetailsByGstid({ gstid, state: stateId });
    if (res.success) setInvoices(res.data.invoice_details || []);
    else alert(res.message || 'Failed to fetch invoice details');
  };

  useEffect(() => {
    loadDetails();
  }, [gstid, stateId]);


  const openRemoveModal = (invoiceId) => {
    setRemoveModal({ open: true, invoiceId, reason: '' });
  };

  const submitRemoveWithReason = async () => {
    if (!removeModal.reason) {
      alert('Please select a reason.');
      return;
    }


    const res = await markInvoiceAsRemoved(removeModal.invoiceId, removeModal.reason);
    if (res.success) {
      alert(res.message || 'Invoice removed');
      setRemoveModal({ open: false, invoiceId: null, reason: '' });
      await loadDetails();
    } else {
      alert(res.message || 'Failed to remove invoice');
    }
  };


  const openEditModal = (id, currentValue) => {
    setEditModal({ open: true, id, value: currentValue || '' });
  };

  const handleEditSubmit = async () => {
    if (!editModal.value?.trim()) {
      alert('Invoice number cannot be empty.');
      return;
    }
    const res = await updateInvoiceNo(editModal.id, { invoice_no: editModal.value.trim() });
    if (res.success) {
      alert('Updated successfully');
      setEditModal({ open: false, id: null, value: '' });
      await loadDetails();
    } else {
      alert(res.message || 'Update failed');
    }
  };


  const handleExport = () => {
    if (!invoices.length) return alert('No data to export');

    const exportData = invoices.map(inv => ({
      Company: inv.company_name,
      'Trans No': inv.trans_no,
      'Ref No': inv.ref_no,
      'Ref Date': inv.ref_date,
      'Invoice Date': inv.invoice_date,
      'Taxable Value': inv.taxable_value,
      CGST: inv.cgst,
      SGST: inv.sgst,
      IGST: inv.igst,
      'Net Amount': inv.net_amount,
      Month: inv.month,
      Branch: inv.branch,
      'Overdue Days': inv.overdue_days
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Invoice_Details.xlsx');
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>UnReconciled ITC Details Vendor wise</h2>

        <div style={{ textAlign: 'right', marginBottom: '10px' }}>
          <button onClick={handleExport} style={{ ...styles.exportButton }}>
            <FaFileExcel style={{ marginRight: '8px' }} /> Export as Excel
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  'Company',
                  'Trans No',
                  'Ref No',
                  'Ref Date',
                  'Invoice Date',
                  'Taxable Value',
                  'CGST',
                  'SGST',
                  'IGST',
                  'Net Amount',
                  'Month',
                  'Branch',
                  'Overdue Days',
                  'Action'
                ].map((col, i) => (
                  <th key={i} style={styles.th}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>{inv.company_name}</td>
                  <td style={styles.td}>{inv.trans_no}</td>
                  <td style={styles.td}>{inv.ref_no}</td>
                  <td style={styles.td}>{inv.ref_date}</td>
                  <td style={styles.td}>{inv.invoice_date}</td>
                  <td style={styles.td}>{inv.taxable_value}</td>
                  <td style={styles.td}>{inv.cgst}</td>
                  <td style={styles.td}>{inv.sgst}</td>
                  <td style={styles.td}>{inv.igst}</td>
                  <td style={styles.td}>{inv.net_amount}</td>
                  <td style={styles.td}>{inv.month}</td>
                  <td style={styles.td}>{inv.branch}</td>
                  <td style={styles.td}>{inv.overdue_days}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        title="Edit Invoice No"
                        style={styles.iconButton}
                        onClick={() => openEditModal(inv.id, inv.ref_no)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        title="Remove with Reason"
                        style={{ ...styles.iconButton, backgroundColor: '#dc3545', color: '#fff' }}
                        onClick={() => openRemoveModal(inv.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!invoices.length && (
                <tr>
                  <td colSpan={14} style={{ ...styles.td, textAlign: 'center', fontStyle: 'italic' }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Edit Invoice Number Modal */}
        {editModal.open && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h4 style={styles.modalTitle}>Edit Invoice Number</h4>
              <input
                type="text"
                value={editModal.value}
                onChange={e => setEditModal({ ...editModal, value: e.target.value })}
                style={styles.modalInput}
                placeholder="Enter new invoice number"
              />
              <div style={styles.modalActions}>
                <button onClick={handleEditSubmit} style={styles.primaryButton}>Submit</button>
                <button onClick={() => setEditModal({ open: false, id: null, value: '' })} style={styles.cancelButton}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {removeModal.open && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h4 style={styles.modalTitle}>Remove Invoice – Select Reason</h4>

              <label style={{ fontSize: '14px', color: '#333' }}>
                Reason
                <select
                  value={removeModal.reason}
                  onChange={(e) => setRemoveModal({ ...removeModal, reason: e.target.value })}
                  style={{ ...styles.modalInput, marginTop: '8px' }}
                >
                  <option disabled>-- Choose a reason --</option>
                  <option value="INELIGIBLE">INELIGIBLE</option>
                  <option value="Cleaning expenses">Cleaning expenses</option>
                  <option value="Mess expenses">Mess expenses</option>
                  <option value="Miscellaneous expenses">Miscellaneous expenses</option>
                  <option value="Office expenses">Office expenses</option>
                  <option value="Refreshments">Refreshments</option>
                  <option value="Repair and maintenance">Repair and maintenance</option>
                  <option value="Sales promotion expenses">Sales promotion expenses</option>
                  <option value="Printing and stationery">Printing and stationery</option>
                  <option value="Telephone expenses">Telephone expenses</option>


                </select>
              </label>

              <div style={styles.modalActions}>
                <button onClick={submitRemoveWithReason} style={styles.dangerButton}>Remove</button>
                <button onClick={() => setRemoveModal({ open: false, invoiceId: null, reason: '' })} style={styles.cancelButton}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: {
    padding: '30px',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    fontFamily: 'Segoe UI, sans-serif'
  },
  heading: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.04)'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1000px'
  },
  th: {
    padding: '12px',
    backgroundColor: '#343a40',
    color: '#fff',
    fontWeight: '500',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6'
  },
  tr: { transition: 'background-color 0.2s' },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    fontSize: '14px',
    backgroundColor: '#fff'
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 10px',
    fontSize: '14px',
    backgroundColor: '#ffc107',
    color: '#000',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  exportButton: {
    padding: '10px 16px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 9999
  },
  modalContent: {
    backgroundColor: '#fff', padding: '30px', borderRadius: '10px',
    width: '90%', maxWidth: '600px', maxHeight: '90vh',
    overflowY: 'auto', boxShadow: '0 0 12px rgba(0,0,0,0.2)'
  },
  modalTitle: { fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#333' },
  modalInput: {
    width: '100%', padding: '12px', fontSize: '16px',
    borderRadius: '6px', border: '1px solid #ccc', marginTop: '12px'
  },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
  primaryButton: { padding: '12px 24px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  cancelButton: { padding: '12px 24px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  dangerButton: { padding: '12px 24px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }
};

export default InvoiceDetailPage;
